import React, { useCallback, useEffect, useState } from 'react';
import SwiperCore from 'swiper';
import {
  StyledDisplay,
  StyledFixedLeaflet,
  StyledLeafletDetail,
} from './styles';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LeafletCategory from './components/LeafletCategory';
import LeafletItem from './components/LeafletItem';
import ShareButton from './components/ShareButton';
import { E_QUERY_KEY } from 'src/utils/queryKey';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGetLeafletDetail, apiGetLeafletList } from 'src/api/apiLeaflet';
import { TConnLoggingInfo, TLeafletItem } from 'src/types/types';
import LeafletPagenation from './components/LeafletPagenation';
import LeafletHeader from './components/LeafletHeader';
import { apiSetLogging } from 'src/api/apiAssistant';
import { IS_LEAFLET_TYPE } from 'src/utils/constants';
import { leafletGaEvent, leafletGaPageView } from 'src/utils/ga';
import { apiPostLog } from 'src/api/apiLog';

type TProps = {
  onClose?: () => void;
};

/**
 * @name 전단 상세
 * @description /leaflet?rst1={점포타입} : 각 점포타입 고정 점포코드로 목록 조회 후 상세 조회
 * @description /{전단번호} : 전단번호로 상세 조회
 * @description /leaflet?str_cd={점포코드} : 점포코드로 상세 조회
 */
function LeafletDetail(props: TProps): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();

  const rst1 = searchParams.get('type') || searchParams.get('rst1'); // 점포타입
  const str_cd = searchParams.get('str_cd'); // 점포코드
  const group_cd = searchParams.get('group_cd'); // 점포그룹코드
  const tabIndex = searchParams.get('tabIndex'); // 탭 번호
  const leaflet_id = searchParams.get('leaflet_id'); // 전단번호(쿼리스트링)

  let leafletId: string | null | undefined; // 조회할 전단번호
  // (~7.30) 그랑그로서리 구리점 하드코딩
  if (rst1 === 'GURI') {
    leafletId = 'GURI';
  } else if (leaflet_id) {
    leafletId = leaflet_id;
  } else {
    if (params.leaflet_id === 'leaflet') {
      leafletId = '';
    } else {
      leafletId = params.leaflet_id;
    }
  }

  const [categorySwiper, setCategorySwiper] = useState<SwiperCore>(); // 카테고리 swiper
  const [contentSwiper, setContentSwiper] = useState<SwiperCore>(); // 전단 swiper
  const [selectedTab, setSelectedTab] = useState(0); // 선택한 카테고리
  const [plateLeafletId, setPlateLeafletId] = useState(leafletId); // 전단 ID

  // 전단 목록 조회
  const { data: leafletList } = useQuery(
    [E_QUERY_KEY.PRODUCT_LIST_LEAFLETLIST],
    () =>
      apiGetLeafletList({
        str_cd: group_cd ? group_cd : str_cd || '', // 점포그룹코드 또는 점포코드
        rst1: group_cd || str_cd ? '' : rst1 || '',
        is_group: group_cd ? '1' : '0', // 0:점포코드 1:점포그룹코드
      }),
    {
      select: data => {
        if (data.plate_leaflet_list && data.plate_leaflet_list.length > 0) {
          return data.plate_leaflet_list[0];
        } else {
          return data;
        }
      },
      onSuccess: data => {
        if (data?.plate_leaflet_id) {
          setPlateLeafletId(data.plate_leaflet_id);
        } else {
          handleNullMove();
        }
      },
      enabled: !plateLeafletId && !!(str_cd || rst1 || group_cd),
    },
  );
  // 전단 상세 조회
  const { data: leafletDetail, isSuccess } = useQuery<TLeafletItem>(
    [
      E_QUERY_KEY.PRODUCT_DETAIL_LEAFLET,
      plateLeafletId,
      leafletList?.plate_leaflet_id,
    ],
    () =>
      apiGetLeafletDetail({
        plate_leaflet_id: plateLeafletId,
      }),
    {
      onError: () => {
        handleNullMove();
      },
      enabled: !!plateLeafletId,
    },
  );

  const setConnLog = useMutation(apiSetLogging); // 외부진입 로그
  const pageConnLog = useMutation(apiPostLog); // 슬라이드별 로그

  // 진행중인 전단 없을 경우 허브 페이지 리다이렉트
  const handleNullMove = useCallback(() => {
    if (IS_LEAFLET_TYPE) {
      navigate('/leaflet/hub', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const setGaEvent = (title: string) => {
    if (!leafletDetail) return;
    const pageName = `전단상세(${leafletDetail.plate_leaflet_nm})`;
    leafletGaEvent(pageName, title);
  };

  // 외부진입 로그 수집
  useEffect(() => {
    if (!leafletDetail) return;
    const hfrom = searchParams.get('hfrom') || '';
    const paramList: TConnLoggingInfo = { hfrom: hfrom };
    [1, 2, 3, 4, 5, 6, 7, 8, 9].map(
      item => (paramList[`option${item}`] = searchParams.get(`rst${item}`)),
    );
    let _pageName = `${leafletDetail.plate_leaflet_nm}-${
      leafletDetail.plate_leaflet_id
    }${paramList.option2 ? `(${paramList.option2})` : '()'}`;

    // GA추가 수집
    if (paramList.option3) {
      _pageName = _pageName + `(${paramList.option3})`;
    }
    leafletGaPageView(_pageName, '');

    if (!IS_LEAFLET_TYPE && hfrom) {
      setConnLog.mutate({
        ...paramList,
      });
    }
    // 데이터 수집 후 쿼리스트링 삭제
    if (!IS_LEAFLET_TYPE) {
      searchParams.delete('hfrom');
      [2, 3, 4, 5, 6, 7, 8, 9].map(item => {
        searchParams.delete(`rst${item}`);
      });
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafletDetail]);

  // 슬라이드별 로그 수집
  useEffect(() => {
    if (!contentSwiper || tabIndex) return;
    const params = {
      screen_id: 7,
      action_id: 15,
      action_detail: leafletDetail?.plate_leaflet_id,
      seq: contentSwiper?.activeIndex + 1,
    };
    pageConnLog.mutate({ ...params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSwiper?.activeIndex, tabIndex]);

  // 첫 진입시 슬라이드 이동
  useEffect(() => {
    if (tabIndex && contentSwiper && contentSwiper.slides) {
      const pageIndex = Number(tabIndex) - 1 < 0 ? 0 : Number(tabIndex) - 1;
      contentSwiper.slideTo(pageIndex);
      searchParams.delete('tabIndex');
      setSearchParams(searchParams, { replace: true });
    }
  }, [tabIndex, contentSwiper, searchParams, setSearchParams]);

  // 전단상세조회 성공시
  useEffect(() => {
    if (isSuccess) {
      if (!leafletDetail && !(str_cd || rst1)) {
        // 진행중인 전단 X 목록 조회 조건 X
        handleNullMove();
      } else if (!leafletDetail) {
        // 진행중인 전단 X 목록 조회 조건 O
        setPlateLeafletId('');
      } else if (leafletDetail.leaflet_img_list.length < 1) {
        // 등록된 전단 이미지 목록 X
        handleNullMove();
      }
    }
  }, [isSuccess, handleNullMove, leafletDetail, str_cd, rst1]);

  useEffect(() => {
    if (!leafletId) return;
    setPlateLeafletId(leafletId);
  }, [leafletId]);

  // 카테고리 유무
  const isCategory =
    leafletDetail &&
    leafletDetail.leaflet_img_list.filter(leaflet => leaflet.img_nm)?.length >
      0;
  return (
    <StyledDisplay>
      <StyledFixedLeaflet>
        <LeafletHeader onClose={props.onClose} />
        {/* 카테고리 */}
        {isSuccess && isCategory && (
          <LeafletCategory
            leafletData={leafletDetail}
            setCategorySwiper={setCategorySwiper}
            contentSwiper={contentSwiper}
            setSelectedTab={setSelectedTab}
            selectedTab={selectedTab}
            setGaEvent={setGaEvent}
          />
        )}
      </StyledFixedLeaflet>
      {isSuccess &&
        leafletDetail &&
        leafletDetail.leaflet_img_list.length > 0 && (
          <StyledLeafletDetail isCategory={isCategory}>
            {/* 전단 */}
            <LeafletItem
              leafletData={leafletDetail}
              categorySwiper={categorySwiper}
              setContentSwiper={setContentSwiper}
              setSelectedTab={setSelectedTab}
              setGaEvent={setGaEvent}
            />
            {/* 페이지네이션 */}
            <LeafletPagenation
              leafletData={leafletDetail}
              contentSwiper={contentSwiper}
              setGaEvent={setGaEvent}
            />
            {/* 공유하기 */}
            <ShareButton
              setGaEvent={setGaEvent}
              contentSwiper={contentSwiper}
              leafletData={leafletDetail}
            />
          </StyledLeafletDetail>
        )}
    </StyledDisplay>
  );
}

export default LeafletDetail;
